/**
 * Volume Renderer for CoandaView
 * GPU-based ray marching volume rendering with transfer functions
 */

class VolumeRenderer {
  constructor(renderingEngine) {
    this.engine = renderingEngine;
    this.volumeMesh = null;
    this.volumeMaterial = null;
    this.volumeTexture3D = null;
    this.transferFunctionTexture = null;
    this.volumeData = null;
    this.transferFunction = null;
  }

  /**
   * Load and display volume data
   */
  async loadVolume(volumeData, transferFunction) {
    try {
      this.volumeData = volumeData;
      this.transferFunction = transferFunction;

      // Create 3D texture from volume data
      this.volumeTexture3D = this.create3DTexture(volumeData);

      // Create transfer function texture (1D)
      this.transferFunctionTexture = this.createTransferFunctionTexture(transferFunction);

      // Create volume shader material
      this.createVolumeMaterial();

      // Create bounding box mesh
      this.createVolumeMesh();

      console.log('Volume loaded successfully:', {
        dimensions: volumeData.dimensions,
        dataRange: volumeData.data_range
      });

      return true;
    } catch (error) {
      console.error('Error loading volume:', error);
      throw error;
    }
  }

  /**
   * Create 3D texture from volume data
   */
  create3DTexture(volumeData) {
    const [nx, ny, nz] = volumeData.dimensions;
    const data = new Float32Array(volumeData.data);

    // THREE.DataTexture3D is the correct name in Three.js r128
    // (newer versions use Data3DTexture)
    const TextureClass = THREE.DataTexture3D || THREE.Data3DTexture;

    if (!TextureClass) {
      throw new Error('3D texture support not available in this Three.js version');
    }

    const texture = new TextureClass(data, nx, ny, nz);
    texture.format = THREE.RedFormat;
    texture.type = THREE.FloatType;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.wrapS = texture.wrapT = texture.wrapR = THREE.ClampToEdgeWrapping;
    texture.unpackAlignment = 1;
    texture.needsUpdate = true;

    return texture;
  }

  /**
   * Create 1D transfer function texture
   */
  createTransferFunctionTexture(transferFunction) {
    const width = 256;
    const data = new Uint8Array(width * 4); // RGBA

    // Interpolate color and opacity from control points
    for (let i = 0; i < width; i++) {
      const t = i / (width - 1);
      const rgba = this.evaluateTransferFunction(t, transferFunction);
      data[i * 4 + 0] = Math.floor(rgba[0] * 255);
      data[i * 4 + 1] = Math.floor(rgba[1] * 255);
      data[i * 4 + 2] = Math.floor(rgba[2] * 255);
      data[i * 4 + 3] = Math.floor(rgba[3] * 255);
    }

    const texture = new THREE.DataTexture(data, width, 1);
    texture.format = THREE.RGBAFormat;
    texture.type = THREE.UnsignedByteType;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.needsUpdate = true;

    return texture;
  }

  /**
   * Evaluate transfer function at parameter t [0, 1]
   */
  evaluateTransferFunction(t, transferFunction) {
    // Interpolate color
    const color = this.interpolateControlPoints(t, transferFunction.color_points, 3);

    // Interpolate opacity
    const opacity = this.interpolateControlPoints(t, transferFunction.opacity_points, 1)[0];

    return [color[0], color[1], color[2], opacity];
  }

  /**
   * Linear interpolation between control points
   */
  interpolateControlPoints(t, points, numChannels) {
    if (points.length === 0) return new Array(numChannels).fill(0);
    if (points.length === 1) {
      const pt = points[0];
      return numChannels === 1 ? [pt[1]] : [pt[1], pt[2], pt[3]];
    }

    // Find surrounding points
    let leftIdx = 0;
    let rightIdx = points.length - 1;

    for (let i = 0; i < points.length - 1; i++) {
      if (t >= points[i][0] && t <= points[i + 1][0]) {
        leftIdx = i;
        rightIdx = i + 1;
        break;
      }
    }

    const left = points[leftIdx];
    const right = points[rightIdx];

    // Linear interpolation parameter
    const localT = (left[0] === right[0]) ? 0 : (t - left[0]) / (right[0] - left[0]);

    if (numChannels === 1) {
      // Opacity interpolation
      return [left[1] + localT * (right[1] - left[1])];
    } else {
      // Color interpolation (RGB)
      return [
        left[1] + localT * (right[1] - left[1]),
        left[2] + localT * (right[2] - left[2]),
        left[3] + localT * (right[3] - left[3])
      ];
    }
  }

  /**
   * Create volume shader material
   */
  createVolumeMaterial() {
    const vertexShader = `
      varying vec3 vPosition;
      varying vec3 vWorldPosition;
      varying vec3 vNormal;

      void main() {
        vPosition = position;
        vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
        vNormal = normalMatrix * normal;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      precision highp sampler3D;

      uniform sampler3D volumeTexture;
      uniform sampler2D transferFunction;
      uniform vec3 volumeDimensions;
      uniform vec3 volumeBounds;
      uniform vec3 volumeOrigin;
      uniform vec3 volumeSpacing;
      uniform vec2 dataRange;
      uniform float stepSize;
      uniform int maxSteps;
      uniform float opacityScale;
      uniform vec3 cameraPos;

      varying vec3 vPosition;
      varying vec3 vWorldPosition;
      varying vec3 vNormal;

      // Ray-box intersection
      vec2 intersectBox(vec3 rayOrigin, vec3 rayDir, vec3 boxMin, vec3 boxMax) {
        vec3 tMin = (boxMin - rayOrigin) / rayDir;
        vec3 tMax = (boxMax - rayOrigin) / rayDir;
        vec3 t1 = min(tMin, tMax);
        vec3 t2 = max(tMin, tMax);
        float tNear = max(max(t1.x, t1.y), t1.z);
        float tFar = min(min(t2.x, t2.y), t2.z);
        return vec2(tNear, tFar);
      }

      void main() {
        // Ray setup
        vec3 rayOrigin = cameraPos;
        vec3 rayDir = normalize(vWorldPosition - cameraPos);

        // Volume bounds in world space
        vec3 boxMin = volumeOrigin;
        vec3 boxMax = volumeOrigin + volumeDimensions * volumeSpacing;

        // Intersect ray with volume bounding box
        vec2 tNearFar = intersectBox(rayOrigin, rayDir, boxMin, boxMax);
        float tNear = max(tNearFar.x, 0.0);
        float tFar = tNearFar.y;

        if (tNear >= tFar || tFar < 0.0) {
          discard;
        }

        // Ray marching
        vec4 color = vec4(0.0);
        float t = tNear;
        float dt = stepSize;

        for (int i = 0; i < 1000; i++) {
          if (i >= maxSteps) break;
          if (t > tFar || color.a > 0.95) break;

          // Current position along ray
          vec3 pos = rayOrigin + rayDir * t;

          // Convert to texture coordinates [0, 1]
          vec3 uvw = (pos - boxMin) / (boxMax - boxMin);

          // Sample volume
          float density = texture(volumeTexture, uvw).r;

          // Normalize density to [0, 1] based on data range
          float normalized = (density - dataRange.x) / (dataRange.y - dataRange.x);
          normalized = clamp(normalized, 0.0, 1.0);

          // Apply transfer function
          vec4 sampleColor = texture2D(transferFunction, vec2(normalized, 0.5));

          // Scale opacity
          sampleColor.a *= opacityScale;

          // Front-to-back compositing
          float sampleAlpha = sampleColor.a * (1.0 - color.a);
          color.rgb += sampleAlpha * sampleColor.rgb;
          color.a += sampleAlpha;

          // Step forward
          t += dt;
        }

        if (color.a < 0.01) {
          discard;
        }

        gl_FragColor = color;
      }
    `;

    // Calculate volume bounds
    const bounds = this.volumeData.bounds;
    const dimensions = this.volumeData.dimensions;
    const spacing = this.volumeData.spacing;
    const origin = this.volumeData.origin;

    this.volumeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        volumeTexture: { value: this.volumeTexture3D },
        transferFunction: { value: this.transferFunctionTexture },
        volumeDimensions: { value: new THREE.Vector3(dimensions[0], dimensions[1], dimensions[2]) },
        volumeBounds: { value: new THREE.Vector3(
          bounds[1] - bounds[0],
          bounds[3] - bounds[2],
          bounds[5] - bounds[4]
        )},
        volumeOrigin: { value: new THREE.Vector3(origin[0], origin[1], origin[2]) },
        volumeSpacing: { value: new THREE.Vector3(spacing[0], spacing[1], spacing[2]) },
        dataRange: { value: new THREE.Vector2(
          this.volumeData.data_range[0],
          this.volumeData.data_range[1]
        )},
        stepSize: { value: 0.01 },
        maxSteps: { value: 256 },
        opacityScale: { value: 1.0 },
        cameraPos: { value: this.engine.camera.position }
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      depthTest: true
    });
  }

  /**
   * Create volume mesh (bounding box)
   */
  createVolumeMesh() {
    const bounds = this.volumeData.bounds;
    const width = bounds[1] - bounds[0];
    const height = bounds[3] - bounds[2];
    const depth = bounds[5] - bounds[4];
    const centerX = (bounds[0] + bounds[1]) / 2;
    const centerY = (bounds[2] + bounds[3]) / 2;
    const centerZ = (bounds[4] + bounds[5]) / 2;

    const geometry = new THREE.BoxGeometry(width, height, depth);
    this.volumeMesh = new THREE.Mesh(geometry, this.volumeMaterial);
    this.volumeMesh.position.set(centerX, centerY, centerZ);
    this.volumeMesh.name = 'volume-render';

    this.engine.scene.add(this.volumeMesh);
  }

  /**
   * Update transfer function
   */
  updateTransferFunction(transferFunction) {
    this.transferFunction = transferFunction;
    if (this.transferFunctionTexture) {
      this.transferFunctionTexture.dispose();
    }
    this.transferFunctionTexture = this.createTransferFunctionTexture(transferFunction);
    if (this.volumeMaterial) {
      this.volumeMaterial.uniforms.transferFunction.value = this.transferFunctionTexture;
    }
  }

  /**
   * Update step size (rendering quality)
   */
  updateStepSize(stepSize) {
    if (this.volumeMaterial) {
      this.volumeMaterial.uniforms.stepSize.value = stepSize;
    }
  }

  /**
   * Update opacity scale
   */
  updateOpacityScale(scale) {
    if (this.volumeMaterial) {
      this.volumeMaterial.uniforms.opacityScale.value = scale;
    }
  }

  /**
   * Update camera position uniform (call in render loop)
   */
  updateCamera() {
    if (this.volumeMaterial && this.engine.camera) {
      this.volumeMaterial.uniforms.cameraPos.value.copy(this.engine.camera.position);
    }
  }

  /**
   * Dispose of volume rendering resources
   */
  dispose() {
    if (this.volumeMesh) {
      this.engine.scene.remove(this.volumeMesh);
      this.volumeMesh.geometry.dispose();
    }

    if (this.volumeMaterial) {
      this.volumeMaterial.dispose();
    }

    if (this.volumeTexture3D) {
      this.volumeTexture3D.dispose();
    }

    if (this.transferFunctionTexture) {
      this.transferFunctionTexture.dispose();
    }

    this.volumeMesh = null;
    this.volumeMaterial = null;
    this.volumeTexture3D = null;
    this.transferFunctionTexture = null;
    this.volumeData = null;
    this.transferFunction = null;
  }

  /**
   * Check if volume is currently loaded
   */
  isLoaded() {
    return this.volumeMesh !== null && this.volumeMaterial !== null;
  }
}

// Export for use in other modules
window.VolumeRenderer = VolumeRenderer;
